import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spinner, ListGroup, TextInput, Label, Select, Tabs, TabItem } from 'flowbite-react';
import { FiPlus, FiDatabase, FiKey, FiRefreshCw, FiTrash } from 'react-icons/fi';
import { FaSyncAlt } from 'react-icons/fa';
import { OAuth2LoginButton } from '../../components/OAuth';
import { useAuth } from '../../hooks/useAuth';
import * as sched from 'sched';
import { z } from 'zod';
import { Formik, Form, Field } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import {
  saveCredential,
  getWorkspaceCredentials,
  deleteCredential,
  StoredCredential,
  CredentialValue,
  credentialProviderLabels,
  credentialTypeLabels
} from '../../utils/credentialStorage';

// Define the data source types
type DataSourceType = 'GitLabGroup' | 'GitLabProject' | 'GitHubRepository';

// Create Data Source Form Values
interface CreateDataSourceFormValues {
  type: DataSourceType;
  fullPath?: string;
  organization?: string;
  repository?: string;
}

// Credential Form Values
interface CreateCredentialFormValues {
  id: string;
  type: 'pat';
  provider: 'github' | 'gitlab';
  value: string;
}

// OAuth2 Provider Configuration
interface OAuth2Provider {
  label: string;
  clientId: string;
  authorizeUrl: string;
  tokenUrl: string;
  scope: string;
}

const DataSourcesPage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { client, token } = useAuth();
  const navigate = useNavigate();

  // State for data sources and credentials
  const [dataSources, setDataSources] = useState<sched.DataSource[]>([]);
  const [credentials, setCredentials] = useState<StoredCredential[]>([]);
  const [loadingDataSources, setLoadingDataSources] = useState(true);
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load data sources and credentials
  const loadData = async () => {
    if (!workspaceId || !token || !client) return;

    try {
      setLoadingDataSources(true);
      setLoadingCredentials(true);
      setError(null);

      // Fetch data sources from API
      const sources = await sched.WrappedApiClient.list_workspace_data_sources(
        client,
        workspaceId,
        token
      );
      setDataSources(sources.data_sources);
      setLoadingDataSources(false);

      // Fetch credentials from IndexedDB
      await loadCredentials();
    } catch (err) {
      setError(`Failed to load data: ${err instanceof Error ? err.message : String(err)}`);
      setLoadingDataSources(false);
      setLoadingCredentials(false);
    }
  };

  // Handle data source deletion
  const handleDeleteDataSource = async (dataSourceId: string) => {
    if (!workspaceId || !token || !client) return;
    if (!window.confirm('Delete this data source? This will remove it from the workspace.')) return;

    try {
      setError(null);
      await sched.WrappedApiClient.delete_workspace_data_source(
        client,
        token,
        workspaceId,
        dataSourceId
      );
      setSuccessMessage('Data source deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadData();
    } catch (err) {
      setError(`Failed to delete data source: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // Separate function to load credentials with retry capability
  const loadCredentials = async () => {
    try {
      const storedCredentials = await getWorkspaceCredentials(workspaceId!);
      setCredentials(storedCredentials);
      setError(null); // Clear any previous credential errors
      setLoadingCredentials(false);
    } catch (credError) {
      console.error('Error loading credentials from IndexedDB:', credError);
      setError(`Failed to load credentials: ${credError instanceof Error ? credError.message : String(credError)}`);
      setLoadingCredentials(false);
    }
  };

  // Initial data load
  useEffect(() => {
    loadData();
  }, [workspaceId, token]);

  // Data source validation schema
  const dataSourceValidationSchema = toFormikValidationSchema(
    z.object({
      type: z.enum(['GitLabGroup', 'GitLabProject', 'GitHubRepository']),
      fullPath: z.string().optional(),
      organization: z.string().optional(),
      repository: z.string().optional(),
    }).refine(data => {
      if (data.type === 'GitLabGroup' || data.type === 'GitLabProject') {
        return !!data.fullPath;
      }
      if (data.type === 'GitHubRepository') {
        return !!data.organization && !!data.repository;
      }
      return false;
    }, {
      message: "Required fields missing for selected data source type",
      path: ["type"]
    })
  );

  // Credential validation schema
  const credentialValidationSchema = toFormikValidationSchema(
    z.object({
      id: z.string().min(1, "ID is required"),
      type: z.enum(['gitlab_pat', 'github_pat'], {
        required_error: "Credential type is required",
        invalid_type_error: "Credential type must be either gitlab_pat or github_pat",
      }),
      value: z.string().min(1, "Value is required"),
    })
  );

  // Initial values for forms
  const initialDataSourceValues: CreateDataSourceFormValues = {
    type: 'GitLabProject',
    fullPath: '',
    organization: '',
    repository: '',
  };

  const initialCredentialValues: CreateCredentialFormValues = {
    id: '',
    type: 'pat',
    provider: 'gitlab',
    value: '',
  };

  // OAuth2 provider configurations
  const oauth2Providers: Record<string, OAuth2Provider> = {
    // Disabled until service-side support is added to Chronomap
    // or CORS headers are added to GitHub
    // {
    //   name: 'github',
    //   label: 'Sign in with GitHub',
    //   clientId: process.env.GITHUB_OAUTH2_CLIENT_ID || '',
    //   authorizeUrl: 'https://github.com/login/oauth/authorize',
    //   tokenUrl: 'https://github.com/login/oauth/access_token'
    // },
    gitlab: {
      label: 'Sign in with GitLab',
      clientId: process.env.GITLAB_OAUTH2_CLIENT_ID || '',
      authorizeUrl: 'https://gitlab.com/oauth/authorize',
      tokenUrl: 'https://gitlab.com/oauth/token',
      scope: 'openid profile email api'
    }
  };

  // Handle data source creation
  const handleCreateDataSource = async (values: CreateDataSourceFormValues, { resetForm }: any) => {
    if (!workspaceId || !token || !client) return;

    try {
      setError(null);

      // Create the configuration object based on the type
      let configuration: sched.DataSourceConfiguration;

      if (values.type === 'GitLabGroup' || values.type === 'GitLabProject') {
        configuration = {
          type: values.type,
          full_path: values.fullPath || '',
        };
      } else {
        configuration = {
          type: 'GitHubRepository',
          organization: values.organization || '',
          repository: values.repository || '',
        };
      }

      // Create the request object
      const request: sched.CreateDataSourceRequest = {
        configuration,
      };

      // Call the API
      await sched.WrappedApiClient.create_workspace_data_source(
        client,
        workspaceId,
        token,
        request
      );

      // Show success message
      setSuccessMessage('Data source created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);

      // Reset the form
      resetForm();

      // Reload data
      loadData();
    } catch (err) {
      setError(`Failed to create data source: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // Handle credential creation/update
  const handleUpsertCredential = async (values: CreateCredentialFormValues, { resetForm }: any) => {
    if (!workspaceId) return;

    try {
      setError(null);

      // Create credential value object based on type
      const credentialValue: CredentialValue = {
        type: values.type,
        provider: values.provider,
        value: values.value
      };

      // Save credential to IndexedDB
      await saveCredential(
        workspaceId,
        values.id,
        JSON.stringify(credentialValue)
      );

      // Show success message
      setSuccessMessage('Credential saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);

      // Reset the form
      resetForm();

      // Reload data
      loadData();
    } catch (err) {
      setError(`Failed to save credential: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // Handle credential deletion
  const handleDeleteCredential = async (credentialId: string) => {
    if (!workspaceId) return;

    if (!window.confirm(`Are you sure you want to delete credential '${credentialId}'?`)) {
      return;
    }

    try {
      setError(null);

      // Delete credential from IndexedDB
      await deleteCredential(credentialId);

      // Show success message
      setSuccessMessage('Credential deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);

      // Reload credentials only
      await loadCredentials();
    } catch (err) {
      setError(`Failed to delete credential: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-cm-text-primary">Data Sources</h1>
        <Button color="blue" onClick={loadData}>
          <FiRefreshCw className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Error and success messages */}
      {error && (
        <Alert color="failure" className="mb-4">
          <div className="flex items-center gap-2">
            <FiRefreshCw className="mr-2" />
            {error}
            {error.includes('Failed to load credentials') && (
              <Button
                size="xs"
                onClick={() => loadCredentials()}
                className="ml-2"
              >
                Retry
              </Button>
            )}
          </div>
        </Alert>
      )}

      {successMessage && (
        <Alert color="success">
          {successMessage}
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data Sources Section */}
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold flex items-center">
              <FiDatabase className="mr-2" /> Data Sources
            </h2>
          </div>

          {loadingDataSources ? (
            <div className="flex justify-center my-8">
              <Spinner size="xl" />
            </div>
          ) : dataSources?.length === 0 ? (
            <div className="text-center text-cm-text-muted my-8">
              No data sources found. Add one below.
            </div>
          ) : (
            <ListGroup>
              {dataSources?.map((source) => (
                <div key={source.id} className="p-3 border-b last:border-b-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">{source.configuration.type}</div>
                      <div className="text-sm text-cm-text-muted">
                        {source.configuration.type === 'GitHubRepository'
                          ? `${source.configuration.organization}/${source.configuration.repository}`
                          : source.configuration.full_path}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        color="light"
                        size="xs"
                        onClick={() => navigate(`/workspaces/${workspaceId}/data-sources/${source.id}/import`)}
                        title="Import data from this source"
                      >
                        <FaSyncAlt />
                      </Button>
                      <Button
                        color="failure"
                        size="xs"
                        onClick={() => handleDeleteDataSource(source.id)}
                        title="Delete data source"
                      >
                        <FiTrash />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </ListGroup>
          )}

          {/* Data Source Form */}
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-4">Add Data Source</h3>
            <Formik
              initialValues={initialDataSourceValues}
              validationSchema={dataSourceValidationSchema}
              onSubmit={handleCreateDataSource}
            >
              {({ values, errors, touched, handleChange, isSubmitting }) => (
                <Form className="space-y-4">
                  <div>
                    <Label htmlFor="type">Source Type</Label>
                    <Field
                      as="select"
                      id="type"
                      name="type"
                      className="bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    >
                      <option value="GitLabProject">GitLab Project</option>
                      <option value="GitLabGroup">GitLab Group</option>
                      <option value="GitHubRepository">GitHub Repository</option>
                    </Field>
                    {errors.type && touched.type && (
                      <div className="text-red-500 text-sm mt-1">{errors.type}</div>
                    )}
                  </div>

                  {(values.type === 'GitLabProject' || values.type === 'GitLabGroup') && (
                    <div>
                      <Label htmlFor="fullPath">Full Path</Label>
                      <TextInput
                        id="fullPath"
                        name="fullPath"
                        type="text"
                        placeholder="e.g., group/project"
                        value={values.fullPath}
                        onChange={handleChange}
                      />
                      {errors.fullPath && touched.fullPath && (
                        <div className="text-red-500 text-sm mt-1">{errors.fullPath}</div>
                      )}
                    </div>
                  )}

                  {values.type === 'GitHubRepository' && (
                    <>
                      <div>
                        <Label htmlFor="organization">Organization</Label>
                        <TextInput
                          id="organization"
                          name="organization"
                          type="text"
                          placeholder="e.g., octocat"
                          value={values.organization}
                          onChange={handleChange}
                        />
                        {errors.organization && touched.organization && (
                          <div className="text-red-500 text-sm mt-1">{errors.organization}</div>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="repository">Repository</Label>
                        <TextInput
                          id="repository"
                          name="repository"
                          type="text"
                          placeholder="e.g., hello-world"
                          value={values.repository}
                          onChange={handleChange}
                        />
                        {errors.repository && touched.repository && (
                          <div className="text-red-500 text-sm mt-1">{errors.repository}</div>
                        )}
                      </div>
                    </>
                  )}

                  <Button type="submit" color="blue" disabled={isSubmitting}>
                    <FiPlus className="mr-2" /> Add Data Source
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
        </Card>

        {/* Credentials Section */}
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold flex items-center">
              <FiKey className="mr-2" /> Credentials
            </h2>
          </div>

          {loadingCredentials ? (
            <div className="flex justify-center my-8">
              <Spinner size="xl" />
            </div>
          ) : credentials?.length === 0 ? (
            <div className="text-center text-cm-text-muted my-8">
              No credentials found. Add one below.
            </div>
          ) : (
            <ListGroup>
              {credentials?.map((credential) => (
                <div key={credential.id} className="p-3 border-b last:border-b-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">{credential.id}</div>
                      <div className="text-sm text-cm-text-muted">
                        {(() => {
                          try {
                            const credentialValue = JSON.parse(credential.value) as CredentialValue;
                            return `${credentialProviderLabels[credentialValue.provider]} ${credentialTypeLabels[credentialValue.type]}`;
                          } catch {
                            return '••••••••••••••••';
                          }
                        })()}
                      </div>
                    </div>
                    <Button
                      color="failure"
                      size="xs"
                      onClick={() => handleDeleteCredential(credential.id)}
                      title="Delete credential"
                    >
                      <FiTrash />
                    </Button>
                  </div>
                </div>
              ))}
            </ListGroup>
          )}

          {/* Credential Form */}
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-4">Add/Update Credential</h3>

            <Tabs>
              <TabItem active title="Personal Access Token">
                <div className="pt-4">
                  <Formik
                    initialValues={initialCredentialValues}
                    validationSchema={credentialValidationSchema}
                    onSubmit={handleUpsertCredential}
                  >
                    {({ errors, touched, handleChange, isSubmitting }) => (
                      <Form className="space-y-4">
                        <div>
                          <Label htmlFor="id">Credential ID</Label>
                          <TextInput
                            id="id"
                            name="id"
                            type="text"
                            placeholder="e.g., github-token"
                            onChange={handleChange}
                          />
                          {errors.id && touched.id && (
                            <div className="text-red-500 text-sm mt-1">{errors.id}</div>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="type">Credential Type</Label>
                          <Field
                            as={Select}
                            id="type"
                            name="type"
                          >
                            <option value="github_pat">GitHub Personal Access Token (PAT)</option>
                            <option value="gitlab_pat">GitLab Personal Access Token (PAT)</option>
                          </Field>
                          {errors.type && touched.type && (
                            <div className="text-red-500 text-sm mt-1">{errors.type}</div>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="value">Credential Value</Label>
                          <TextInput
                            id="value"
                            name="value"
                            type="password"
                            placeholder="Enter token or password"
                            onChange={handleChange}
                          />
                          {errors.value && touched.value && (
                            <div className="text-red-500 text-sm mt-1">{errors.value}</div>
                          )}
                        </div>

                        <Button type="submit" color="blue" disabled={isSubmitting}>
                          <FiPlus className="mr-2" /> Save Credential
                        </Button>
                      </Form>
                    )}
                  </Formik>
                </div>
              </TabItem>

              <TabItem title="OAuth2 Login">
                <div className="pt-4 space-y-4">
                  <p className="text-sm text-cm-text-primary">
                    Connect with OAuth2 providers to access repositories and other resources.
                    The token will be stored securely in your browser.
                  </p>

                  <div className="space-y-3">
                    {Object.entries(oauth2Providers).map(([name, provider]) => (
                      <div key={name}>
                        <OAuth2LoginButton
                          label={provider.label}
                          clientId={provider.clientId}
                          issuer={name}
                          scope={provider.scope}
                          authorizeUrl={provider.authorizeUrl}
                          redirectUrl={`${window.location.origin}/oauth2/callback/${name}`}
                          className="w-full"
                        />
                      </div>
                    ))}
                  </div>

                  <Alert color="info" className="mt-4">
                    <p className="text-sm">
                      After successful authentication, a credential will be created with ID format:
                      <code className="bg-cm-bg-hover px-1 py-0.5 rounded">{`provider[username]`}</code>
                    </p>
                  </Alert>
                </div>
              </TabItem>
            </Tabs>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DataSourcesPage;
